/**
 * UserDocumentsPage Component
 * 
 * Página para o usuário gerenciar seus próprios documentos.
 * 
 * Funcionalidades:
 * - Lista de documentos essenciais (categorias obrigatórias)
 * - Campos de upload por categoria
 * - Indicadores de documentos obrigatórios
 * - Campo adicional para outros documentos
 * - Visualização, edição e exclusão de documentos
 */

import { useState, useEffect } from 'react'
import {
  FileText,
  Plus,
  Upload,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Tag,
  X,
  Send,
  Loader2,
  FolderOpen,
  FileCheck,
  Clock
} from 'lucide-react'
import { myDocumentService, documentCategoryService } from '../../../services/documentsApi'
import { DocumentUpload, DocumentCard } from '../../../components/Documents'
import Button from '../../../components/Button/Button'
import Card from '../../../components/Card/Card'
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal'
import Modal from '../../../components/ERP/Modal/Modal'
import './UserDocumentsPage.css'

function UserDocumentsPage() {
  const [documents, setDocuments] = useState([])
  const [categories, setCategories] = useState([])
  const [missingCategories, setMissingCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState({})
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState(null)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadFormData, setUploadFormData] = useState({
    category_id: '',
    title: '',
    expiry_date: ''
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      // Carregar categorias primeiro para poder associar aos documentos
      const categoriesResponse = await documentCategoryService.getAll({ type: 'user' })
      let categoriesData = []
      if (categoriesResponse.success) {
        // A API retorna { success: true, data: { categories: [...] } }
        categoriesData = categoriesResponse.data?.categories || 
                              (Array.isArray(categoriesResponse.data) ? categoriesResponse.data : [])
        // Normalizar campos booleanos que podem vir como string do banco
        categoriesData = categoriesData.map(cat => ({
          ...cat,
          required: cat.required === true || cat.required === 'true' || cat.required === 1 || cat.required === '1',
          allow_multiple: cat.allow_multiple === true || cat.allow_multiple === 'true' || cat.allow_multiple === 1 || cat.allow_multiple === '1',
          active: cat.active !== false && cat.active !== 'false' && cat.active !== 0 && cat.active !== '0'
        }))
        setCategories(categoriesData)
      } else {
        console.error('Erro ao carregar categorias:', categoriesResponse)
        setCategories([])
      }

      // Carregar documentos do usuário
      const docsResponse = await myDocumentService.getAll()
      if (docsResponse.success) {
        const docs = docsResponse.data?.documents || []
        // Associar categorias aos documentos
        const docsWithCategories = docs.map(doc => {
          if (doc.category_id) {
            const category = categoriesData.find(cat => {
              const catId = parseInt(cat.id)
              const docCatId = parseInt(doc.category_id)
              return catId === docCatId
            })
            if (category) {
              return { ...doc, category }
            }
          }
          return doc
        })
        setDocuments(docsWithCategories)
        setMissingCategories(docsResponse.data?.missing_categories || [])
      } else {
        console.error('Erro ao carregar documentos:', docsResponse)
      }
    } catch (error) {
      console.error('Erro ao carregar documentos:', error)
      // Se for erro de permissão, mostrar mensagem mais clara
      if (error.response?.status === 403) {
        console.error('Acesso negado. Verifique suas permissões.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUpload = async (categoryId, file, title = null, expiryDate = null) => {
    if (!file) {
      alert('Por favor, selecione um arquivo')
      return
    }

    setUploading({ ...uploading, [categoryId || 'other']: true })

    try {
      const data = {
        category_id: categoryId || undefined,
        title: title || undefined,
        expiry_date: expiryDate || undefined
      }

      const response = await myDocumentService.upload(file, data)

      if (response.success) {
        await loadData() // Recarregar documentos
        setUploading({ ...uploading, [categoryId || 'other']: false })
      } else {
        alert(response.message || 'Erro ao enviar documento')
        setUploading({ ...uploading, [categoryId || 'other']: false })
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error)
      alert(error.response?.data?.message || 'Erro ao fazer upload do documento')
      setUploading({ ...uploading, [categoryId || 'other']: false })
    }
  }

  const handleDelete = (document) => {
    setDocumentToDelete(document)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = async () => {
    if (!documentToDelete) return

    try {
      const response = await myDocumentService.delete(documentToDelete.id)
      if (response.success) {
        setDeleteConfirmOpen(false)
        setDocumentToDelete(null)
        await loadData()
      } else {
        alert(response.message || 'Erro ao deletar documento')
      }
    } catch (error) {
      console.error('Erro ao deletar documento:', error)
      alert(error.response?.data?.message || 'Erro ao deletar documento')
    }
  }

  const handleDownload = async (doc) => {
    if (doc.file_url) {
      try {
        // Determinar nome do arquivo com extensão
        let fileName = doc.file_name || 'documento'
        
        // Se não tiver extensão, tentar extrair da URL ou usar baseado no tipo
        if (!fileName.includes('.')) {
          // Tentar extrair extensão da URL
          const urlExtension = doc.file_url.split('.').pop()?.split('?')[0]
          if (urlExtension && urlExtension.length <= 5) {
            fileName = `${fileName}.${urlExtension}`
          } else if (doc.file_type) {
            // Usar tipo MIME para determinar extensão
            const mimeToExt = {
              'application/pdf': 'pdf',
              'image/jpeg': 'jpg',
              'image/jpg': 'jpg',
              'image/png': 'png',
              'image/gif': 'gif',
              'application/msword': 'doc',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx'
            }
            const ext = mimeToExt[doc.file_type]
            if (ext) {
              fileName = `${fileName}.${ext}`
            }
          }
        }

        // Criar um link temporário para download
        const link = document.createElement('a')
        link.href = doc.file_url
        link.download = fileName
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('Erro ao fazer download:', error)
        // Fallback: abrir em nova aba se o download direto falhar
        window.open(doc.file_url, '_blank')
      }
    }
  }

  // Obter todos os documentos de uma categoria (para categorias que permitem múltiplos)
  const getDocumentsByCategory = (categoryId) => {
    return documents.filter(doc => {
      const docCategoryId = doc.category_id ? parseInt(doc.category_id) : null
      const catId = categoryId ? parseInt(categoryId) : null
      return docCategoryId === catId
    })
  }

  // Obter categorias essenciais (obrigatórias)
  const essentialCategories = Array.isArray(categories) ? categories.filter(cat => cat.required) : []
  const otherCategories = Array.isArray(categories) ? categories.filter(cat => !cat.required) : []

  // Obter documentos que não estão em categorias (documentos adicionais)
  const otherDocuments = documents.filter(doc => !doc.category_id)
  
  // Obter documentos de categorias não obrigatórias que têm documentos
  const otherCategoryDocuments = otherCategories
    .map(category => ({
      category,
      documents: getDocumentsByCategory(category.id)
    }))
    .filter(item => item.documents.length > 0)

  if (loading) {
    return (
      <div className="user-documents-page">
        <div className="user-documents-page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <FileText size={28} />
            <div>
              <h1>Meus Documentos</h1>
              <p>Gerencie seus documentos pessoais</p>
            </div>
          </div>
        </div>
        <div className="loading" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
          <Loader2 size={20} className="spinning" />
          <span>Carregando...</span>
        </div>
      </div>
    )
  }

  const handleOpenUploadModal = () => {
    setUploadModalOpen(true)
    setSelectedFile(null)
    setUploadFormData({
      category_id: '',
      title: '',
      expiry_date: ''
    })
  }

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false)
    setSelectedFile(null)
    setUploadFormData({
      category_id: '',
      title: '',
      expiry_date: ''
    })
  }

  const handleFileSelect = (file) => {
    setSelectedFile(file)
  }

  const handleFileRemove = () => {
    setSelectedFile(null)
  }

  const handleSubmitUpload = async () => {
    if (!selectedFile) {
      alert('Por favor, selecione um arquivo')
      return
    }

    // Validar que category_id foi fornecido (agora é obrigatório)
    if (!uploadFormData.category_id) {
      alert('Por favor, selecione uma categoria para o documento')
      return
    }

    setUploading({ ...uploading, modal: true })

    try {
      const data = {
        category_id: uploadFormData.category_id || undefined,
        title: uploadFormData.title || undefined,
        expiry_date: uploadFormData.expiry_date || undefined,
        force_create: true // Sempre criar novo documento quando adicionar via modal
      }

      const response = await myDocumentService.upload(selectedFile, data)

      if (response.success) {
        await loadData()
        handleCloseUploadModal()
        alert('Documento enviado com sucesso!')
      } else {
        alert(response.message || 'Erro ao enviar documento')
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error)
      alert(error.response?.data?.message || 'Erro ao fazer upload do documento')
    } finally {
      setUploading({ ...uploading, modal: false })
    }
  }

  return (
    <div className="user-documents-page">
      <div className="user-documents-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <FileText size={28} />
          <div>
            <h1>Meus Documentos</h1>
            <p>Faça upload dos seus documentos pessoais</p>
          </div>
        </div>
      </div>

      {/* Seção de Documentos Essenciais */}
      <div className="user-documents-section">
        <h2 className="user-documents-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FileCheck size={24} />
          Documentos Essenciais {missingCategories.length > 0 && (
            <span className="user-documents-badge" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <AlertTriangle size={14} />
              Faltam {missingCategories.length}
            </span>
          )}
        </h2>
        <p className="user-documents-section-description">
          Estes documentos são obrigatórios e devem ser enviados
        </p>

        <div className="user-documents-essential-grid">
          {essentialCategories.map((category) => {
            const categoryDocuments = getDocumentsByCategory(category.id)
            const hasDocuments = categoryDocuments.length > 0
            const isMissing = missingCategories.some(mc => mc.id === category.id)
            const isUploading = uploading[category.id]
            const allowsMultiple = category.allow_multiple || false

            return (
              <Card key={category.id} variant="outlined" className="user-documents-category-card">
                <div className="user-documents-category-header">
                  <h3 className="user-documents-category-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FolderOpen size={18} />
                    {category.name}
                  </h3>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {category.required && (
                      <span className="user-documents-required-badge" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <AlertTriangle size={12} />
                        Obrigatório
                      </span>
                    )}
                    {allowsMultiple && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.25rem 0.5rem', background: '#d1fae5', color: '#065f46', borderRadius: '4px', fontSize: '0.75rem' }}>
                        <CheckCircle size={12} />
                        Múltiplos permitidos
                      </span>
                    )}
                    {hasDocuments && (
                      <span className="user-documents-uploaded-badge" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <CheckCircle size={12} />
                        {categoryDocuments.length} {categoryDocuments.length === 1 ? 'enviado' : 'enviados'}
                      </span>
                    )}
                    {isMissing && (
                      <span className="user-documents-missing-badge" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <AlertTriangle size={12} />
                        Faltante
                      </span>
                    )}
                  </div>
                </div>

                {category.description && (
                  <p className="user-documents-category-description">{category.description}</p>
                )}

                {hasDocuments ? (
                  <div className="user-documents-existing">
                    <div className="user-documents-grid" style={{ marginTop: '1rem' }}>
                      {categoryDocuments.map((doc) => (
                        <DocumentCard
                          key={doc.id}
                          document={doc}
                          onView={handleDownload}
                          onDelete={handleDelete}
                          showActions={true}
                          className="user-documents-existing-card"
                        />
                      ))}
                    </div>
                    {allowsMultiple && (
                      <div className="user-documents-upload-area" style={{ marginTop: '1rem' }}>
                        <DocumentUpload
                          onFileSelect={(file) => handleUpload(category.id, file)}
                          disabled={isUploading}
                        />
                        {isUploading && (
                          <p className="user-documents-uploading" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                            <Loader2 size={16} className="spinning" />
                            Enviando...
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="user-documents-upload-area">
                    <DocumentUpload
                      onFileSelect={(file) => handleUpload(category.id, file)}
                      disabled={isUploading}
                    />
                    {isUploading && (
                      <p className="user-documents-uploading" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                        <Loader2 size={16} className="spinning" />
                        Enviando...
                      </p>
                    )}
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      </div>

      {/* Seção de Outros Documentos */}
      <div className="user-documents-section">
        <div className="user-documents-section-header">
          <div>
            <h2 className="user-documents-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FileText size={24} />
              Outros Documentos
            </h2>
            <p className="user-documents-section-description">
              Envie documentos adicionais como atestados médicos, certidões, etc.
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={handleOpenUploadModal}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Plus size={18} />
            Adicionar Documento
          </Button>
        </div>

        {otherDocuments.length > 0 && (
          <div className="user-documents-other-list">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CheckCircle size={20} />
              Documentos Adicionais Enviados
            </h3>
            <div className="user-documents-grid">
              {otherDocuments.map((document) => (
                <DocumentCard
                  key={document.id}
                  document={document}
                  onView={handleDownload}
                  onDelete={handleDelete}
                  showActions={true}
                />
              ))}
            </div>
          </div>
        )}

        {/* Documentos de outras categorias */}
        {otherCategoryDocuments.length > 0 && (
          <div className="user-documents-other-list" style={{ marginTop: '2rem' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Tag size={20} />
              Documentos por Categoria
            </h3>
            <div className="user-documents-grid">
              {otherCategoryDocuments.map(({ category, documents: categoryDocs }) => (
                categoryDocs.map((document) => (
                  <DocumentCard
                    key={document.id}
                    document={document}
                    onView={handleDownload}
                    onDelete={handleDelete}
                    showActions={true}
                  />
                ))
              ))}
            </div>
          </div>
        )}

      </div>

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
          setDocumentToDelete(null)
        }}
        onConfirm={confirmDelete}
        title="Confirmar Exclusão"
        message={`Deseja realmente excluir o documento "${documentToDelete?.title || documentToDelete?.category?.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="danger"
      />

      {/* Modal de Upload de Documentos */}
      <Modal
        isOpen={uploadModalOpen}
        onClose={handleCloseUploadModal}
        title="Adicionar Documento Pessoal"
        size="medium"
        footer={
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              onClick={handleCloseUploadModal}
              disabled={uploading.modal}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <X size={18} />
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitUpload}
              disabled={!selectedFile || uploading.modal}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              {uploading.modal ? (
                <>
                  <Loader2 size={18} className="spinning" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Enviar Documento
                </>
              )}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Upload de Arquivo */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontWeight: 500 }}>
              <Upload size={18} />
              Arquivo <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <DocumentUpload
              onFileSelect={handleFileSelect}
              onFileRemove={handleFileRemove}
              selectedFile={selectedFile}
              disabled={uploading.modal}
            />
          </div>

          {/* Categoria */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontWeight: 500 }}>
              <Tag size={18} />
              Categoria <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <select
              value={uploadFormData.category_id}
              onChange={(e) => setUploadFormData({ ...uploadFormData, category_id: e.target.value })}
              disabled={uploading.modal}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="">Selecione uma categoria</option>
              {categories.filter(cat => cat.active !== false).map((category) => (
                <option key={category.id} value={String(category.id)}>
                  {category.name} {category.required && '(Obrigatório)'}
                </option>
              ))}
            </select>
            <p style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Clock size={12} />
              Selecione a categoria do documento
            </p>
          </div>

          {/* Título */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontWeight: 500 }}>
              <FileText size={18} />
              Título do Documento (opcional)
            </label>
            <input
              type="text"
              value={uploadFormData.title}
              onChange={(e) => setUploadFormData({ ...uploadFormData, title: e.target.value })}
              disabled={uploading.modal}
              placeholder="Ex: Atestado Médico, Certidão de Nascimento, etc."
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            />
            <p style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#6b7280' }}>
              Título adicional para o documento (opcional)
            </p>
          </div>

          {/* Data de Expiração */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontWeight: 500 }}>
              <Calendar size={18} />
              Data de Expiração (opcional)
            </label>
            <input
              type="date"
              value={uploadFormData.expiry_date}
              onChange={(e) => setUploadFormData({ ...uploadFormData, expiry_date: e.target.value })}
              disabled={uploading.modal}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default UserDocumentsPage

